import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  { label: "All Jewelry", value: "all" },
  { label: "Necklaces", value: "necklaces" },
  { label: "Earrings", value: "earrings" },
  { label: "Bracelets", value: "bracelets" },
  { label: "Rings", value: "rings" },
];

const SORTS = [
  { label: "Featured", value: "featured" },
  { label: "Newest", value: "newest" },
  { label: "Price: Low to High", value: "price-asc" },
  { label: "Price: High to Low", value: "price-desc" },
  { label: "Top Rated", value: "rating" },
];

const COLLECTION_TITLES = {
  "new-arrivals": "New Arrivals",
  "best-sellers": "Best Sellers",
  necklaces: "Necklaces",
  earrings: "Earrings",
  bracelets: "Bracelets",
  rings: "Rings",
};

export default function Shop() {
  const { slug } = useParams(); // for /collection/:slug
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") || "";

  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("featured");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const isCollection = Boolean(slug);
  const title = isCollection
    ? COLLECTION_TITLES[slug] || "Collection"
    : search
    ? `Results for “${search}”`
    : "All Jewelry";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (isCollection) params.collection = slug;
      else {
        if (category !== "all") params.category = category;
        if (search) params.search = search;
      }
      const { data } = await api.get("/products", { params });
      setProducts(data);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [sort, category, search, slug, isCollection]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-ivory" data-testid="shop-page">
      {/* Header */}
      <div className="border-b border-black/5 bg-cream px-5 py-16 md:px-10 md:py-24">
        <div className="mx-auto max-w-[1400px]">
          <p className="eyebrow text-gold">{isCollection ? "Collection" : "Shop"}</p>
          <h1 className="mt-4 font-serif text-5xl font-light tracking-tight text-ink md:text-7xl" data-testid="shop-title">
            {title}
          </h1>
        </div>
      </div>

      {/* Controls */}
      <div className="sticky top-[57px] z-30 border-b border-black/5 bg-ivory/85 backdrop-blur-xl md:top-[113px]">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-5 py-4 md:px-10">
          {!isCollection ? (
            <div className="flex flex-wrap gap-2" data-testid="category-filters">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setCategory(c.value)}
                  data-testid={`filter-${c.value}`}
                  className={`rounded-full px-4 py-2 text-[0.68rem] uppercase tracking-[0.18em] transition-colors duration-300 ${
                    category === c.value
                      ? "bg-ink text-ivory"
                      : "border border-sand text-ink/70 hover:border-ink"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          ) : (
            <span className="text-sm text-ink/60" data-testid="shop-count">
              {products.length} pieces
            </span>
          )}

          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[190px] rounded-full border-sand bg-ivory text-[0.68rem] uppercase tracking-[0.18em]" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => (
                <SelectItem key={s.value} value={s.value} data-testid={`sort-${s.value}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Grid */}
      <div className="mx-auto max-w-[1400px] px-5 py-16 md:px-10">
        {loading ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-4 md:gap-x-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/5] rounded-2xl bg-beige" />
                <div className="mt-4 h-4 w-2/3 rounded bg-beige" />
                <div className="mt-2 h-3 w-1/3 rounded bg-beige" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <Reveal className="py-24 text-center">
            <p className="font-serif text-3xl text-ink">Nothing here yet.</p>
            <p className="mt-3 text-ink/60">Try another category or search.</p>
          </Reveal>
        ) : (
          <div className="grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-4 md:gap-x-6">
            {products.map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
